import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import disableInFastboot from '@triptyk/ember-utils/decorators/disable-in-fastboot';
import type FastbootService from 'ember-cli-fastboot';
import type FlashMessageService from 'ember-cli-flash/services/flash-messages';
import type IntlService from 'ember-intl/services/intl';
import type ActionModel from 'frontend/models/action';
import type ProductModel from 'frontend/models/product';
import { getProductIdFromModel } from 'frontend/utils/get-product-id-from-model';
import { tracked } from 'tracked-built-ins';

interface FormationPartsFloatingMenuComponentArgs {
  formation: ProductModel | ActionModel;
}

export default class FormationPartsFloatingMenuComponent extends Component<FormationPartsFloatingMenuComponentArgs> {
  @tracked isOpened = false;
  @tracked isOpenedModal = false;
  @tracked isSendToFriend = false;
  @service declare flashMessages: FlashMessageService;
  @service declare fastboot: FastbootService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  get productId() {
    return getProductIdFromModel(this.args.formation);
  }

  get actualUrl() {
    if (!this.fastboot.isFastBoot) {
      // eslint-disable-next-line no-restricted-globals
      return window.location.href;
    }
    return '';
  }

  get emailHref() {
    return `mailto:?body=${this.shareText}&subject=${this.intl.t(
      'components.pages.formations.floatingMenu.share_email_subject',
      {
        name: this.args.formation.name,
      }
    )}`;
  }

  get shareText() {
    return `${this.intl.t(
      'components.pages.formations.floatingMenu.share_text_before'
    )} "${this.args.formation.name}" ${this.intl.t(
      'components.pages.formations.floatingMenu.share_text_after'
    )} ${this.actualUrl}`;
  }

  @action showMenu() {
    this.isOpened = !this.isOpened;
  }

  @action openModal(type: string) {
    if (type === 'shareToFriend') this.isSendToFriend = true;
    this.isOpenedModal = !this.isOpenedModal;
    this.isOpened = false;
  }

  @action closeModal() {
    this.isOpenedModal = !this.isOpenedModal;
    this.isSendToFriend = false;
  }

  @action
  @disableInFastboot
  async copyUrl() {
    try {
      await navigator.clipboard.writeText(this.actualUrl);
      this.flashMessages.success(
        this.intl.t('components.pages.formations.floatingMenu.link_copied')
      );
    } catch {
      this.flashMessages.danger(
        this.intl.t('components.pages.formations.floatingMenu.link_fail')
      );
    }
  }

  @action
  print() {
    if (this.fastboot.isFastBoot) {
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    window.print();
  }
}
